import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import * as css from './CaseStudy.m.css';
import { Summary } from '../../widgets/Summary';
import ContentWrapper from '../../widgets/ContentWrapper';
import SpeechBubble from '../../widgets/SpeechBubble';
import Technologies from '../../widgets/Technologies';
import ImageSection from '../../widgets/ImageSection';
import Section from '../../widgets/Section';
import ConnectStrip from '../../widgets/ConnectStrip';
import { Text } from '../../widgets/Text';
import Image from '../../widgets/Image';

const builtTop = require('../../assets/images/work-mockups/built-card-top-right.png');
const builtTopHighRes = require('../../assets/images/work-mockups/built-card-top-right@2x.png');
const builtMain = require('../../assets/images/work-mockups/built-mockup.png');
const builtMainHighRes = require('../../assets/images/work-mockups/built-mockup@2x.png');
const builtBottomLeft = require('../../assets/images/work-mockups/built-card-bottom-left.png');
const builtBottomLeftHighRes = require('../../assets/images/work-mockups/built-card-bottom-left@2x.png');
const builtBottomRight = require('../../assets/images/work-mockups/built-card-bottom-right.png');
const builtBottomRightHighRes = require('../../assets/images/work-mockups/built-card-bottom-right@2x.png');

const factory = create();

export const CaseStudyBuilt = factory(function CaseStudyBuilt() {
	return (
		<div classes={css.root}>
			<Head
				title="Custom Web Application: Built Technologies | SitePen"
				description="Learn how we helped Built Technologies improve their enterprise-level software solution and accelerate key capabilities for their users."
			/>
			<Hero background="level1PurpleTeal" align="center" paddingBottom="sm">
				{{
					title: 'Case Study',
					xlTitle: 'Built Technologies',
					blurb: (
						<virtual>
							<span>
								It’s rare that a technology startup successfully creates a product
								that solves real-world problems and engages customers on their first
								try. So what happens when that very success creates growing pains
								for the company?
							</span>
							<Technologies
								icons={['react', 'typescript', 'intern', 'canjs', 'php']}
							></Technologies>
						</virtual>
					)
				}}
			</Hero>
			<ContentWrapper size="small">
				<Text type="section">The Situation</Text>
				<Text type="body">
					Built Technologies offers the leading software for construction lenders. Built
					creates a safe, secure digital tool to help customers reduce risk when funding
					complex development projects and gives builders better cash flow management
					oversight.
				</Text>
				<Text type="body">
					Because demand for its unique application features was increasing quickly, Built
					needed to rapidly scale its existing platform while reengineering their next
					generation solution.
				</Text>
			</ContentWrapper>
			<ImageSection shaded paddingTop="lg" contentBias>
				{{
					title: 'The Solution',
					blurb: (
						<div classes={[css.stackedBlurb]}>
							<Text type="body">
								SitePen worked with Built Technologies to meet competing priorities
								which included legacy application maintenance and next gen build out
								to proactively meet the demands of continued business growth. In
								addition to conducting a full TypeScript modernization and
								conversion to improve developer ergonomics and ensure best
								practices, we also established a proactive, integrated testing
								solution and put the right tools in place to identify problematic
								patterns to prevent the accumulation of technical debt going
								forward. These were significant DevOps refinements that would drive
								efficiency and long-term sustainability across platforms, laying the
								foundation for its maturing development team.
							</Text>
							<Text type="body">
								SitePen’s development approach prioritized architecture, reusable
								components and engrained best practices to help speed future
								development efforts within Built Technologies’ development
								discipline and design system guidelines.
							</Text>
						</div>
					),
					images: {
						main: (
							<div classes={[css.stackedMainImage]}>
								<Image
									src={builtMain}
									highResSrc={builtMainHighRes}
									shadow="light"
								/>
							</div>
						),
						top: (
							<Image
								src={builtTop}
								highResSrc={builtTopHighRes}
								shadow="dark"
								size="lg"
							/>
						),
						bottom: {
							left: (
								<Image
									src={builtBottomLeft}
									highResSrc={builtBottomLeftHighRes}
									shadow="dark"
									size="lg"
								/>
							),
							right: (
								<Image
									src={builtBottomRight}
									highResSrc={builtBottomRightHighRes}
									shadow="dark"
									size="lg"
								/>
							)
						}
					}
				}}
			</ImageSection>
			<Section type="leading">
				<ContentWrapper>
					<Summary collapse="stackReverse" imageGutter="medium" trailingBias="small">
						{{
							title: 'The Result',
							blurb:
								'Built Technologies could more quickly add critical features to meet customer needs while maintaining a stable code base. Built now has a clear path to transition from aging technologies to a more modern, nimble architecture.',
							image: (
								<SpeechBubble>
									{{
										main:
											'SitePen not only had the technical enterprise application expertise, but worked closely with us to find the right platform that would meet our requirements. They went beyond the initial scope and identified a testing gap that really added value to our operations.',
										subtitle1: 'Matthew Russell',
										subtitle2: 'Then-CTO, Built'
									}}
								</SpeechBubble>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section type="trailing" shaded>
				<ConnectStrip trailing />
			</Section>
		</div>
	);
});

export default CaseStudyBuilt;
